import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Footer from '../home/components/Footer';
import { useTranslation } from '../../hooks/useTranslation';
import LanguageSwitcher from '../../components/ui/LanguageSwitcher';
import { fetchLatestTenderDate, fetchLastCollectedAt, fetchMandiIndex, fetchTenderBlock, fetchFactoryDirectoryIndex } from './data';
import { getISTDateParts, istMidnightISO, addDays, compareDateParts, laterOf } from './dateWindow';
import { buildBuyNowUrl } from './matching';
import { LOGO_URL } from '../market-intelligence/shareExport';

const NAVY = '#0F2238';
const AMBER = '#F59E0B';
const AMBER_DEEP = '#B45309';
const CREAM = '#FDF8F0';
const BORDER = '#E7E0D3';
const MUTED = '#78716C';
const FAINT = '#A8A29E';
// SugarHathi brand blue — the same token used for the logo gradient and nav
// accents across the app (see RoleBasedHeader.jsx / tailwind.css), reused here
// instead of the old green band. BLUE_TINT is the matching light background
// (Tailwind blue-50), already paired with this exact blue elsewhere in the app.
const BLUE = '#1e4a72';
const BLUE_TINT = '#EFF6FF';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const fmtRate = (n) => (n != null ? `₹${n.toLocaleString('en-IN')}/MT` : '');

// Rounds down to the hour — an honest "market intelligence" precision, never
// a false claim of minute-level live data. Full timestamps (with seconds,
// where the source has them) are preserved everywhere else for sorting/logic/
// grouping; only display strings are ever rounded.
const fmtApproxHour = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const hour = d.getHours();
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${hour12} ${ampm}`;
};

const formatUpdatedAround = (iso) => {
  if (!iso) return null;
  const day = new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return `${day}, ${fmtApproxHour(iso)}`;
};

// Every tender_date_time is a distinct market data point, so the presentation group
// is Factory + EXACT tender_date_time — never Factory alone, and never collapsed
// down to "latest per factory+grade". The same factory quoting the same grade on
// two different dates produces two separate groups, both preserved.
//
// Within one group (already the same factory, same tender event), a field is
// "shared" only when every visible grade in that group has the exact same
// non-empty value for it — determined from the actual values present, never
// assumed. When not shared, each grade carries its own value instead.
const buildTenderEventGroups = (records) => {
  const order = [];
  const byEvent = new Map();
  records.forEach((r) => {
    const key = `${r.millName}|||${r.tenderDateTime}`;
    if (!byEvent.has(key)) {
      byEvent.set(key, []);
      order.push(key);
    }
    byEvent.get(key).push(r);
  });

  const allShared = (values) => values.length > 0 && values.every((v) => !!v) && values.every((v) => v === values[0]);

  return order.map((key) => {
    const group = byEvent.get(key);
    const seasons = group.map((r) => r.season);
    const paymentDates = group.map((r) => r.paymentDate);
    const liftingDates = group.map((r) => r.liftingDate);

    return {
      key,
      millName: group[0].millName,
      state: group[0].state,
      tenderDateTime: group[0].tenderDateTime,
      directoryFactorySlug: group[0].directoryFactorySlug,
      records: group,
      sharedSeason: allShared(seasons) ? seasons[0] : null,
      sharedPaymentDate: allShared(paymentDates) ? paymentDates[0] : null,
      sharedLiftingDate: allShared(liftingDates) ? liftingDates[0] : null,
    };
  });
};

const GlobalStyle = () => (
  <style>{`
    @keyframes cardIn { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
    .card-enter { animation: cardIn .38s cubic-bezier(.22,1,.36,1) both; }
    select:disabled, input:disabled { cursor: not-allowed; opacity: 0.6; }
  `}</style>
);

const BuyNowButton = ({ tender, navigate, t, compact }) => {
  if (!tender.matched) return null;
  return (
    <button
      onClick={() => navigate(buildBuyNowUrl({ mandiFactorySlug: tender.mandiFactorySlug, mandiGradeCode: tender.mandiGradeCode, mandiQuoteId: tender.mandiQuoteId }))}
      className={`inline-flex items-center gap-1.5 rounded-xl font-bold text-white bg-[#ff8c00] hover:bg-[#f07d00] shadow-lg hover:shadow-[#ff8c00]/40 hover:shadow-xl transition-all duration-200 flex-shrink-0 ${compact ? 'px-3 py-1.5 text-[11px]' : 'px-3.5 py-2 text-xs'}`}
    >
      <Icon name="ShoppingBag" size={12} color="#fff" />
      {t('liveTenderInfo.buyNow', 'Buy Now on Mandi')}
    </button>
  );
};

// Factory name as a link to its dedicated factory page (/market-intelligence/
// factory/:slug — the existing, mi_mills-backed public factory page, present
// for every directory factory regardless of whether SugarHathi has a live
// Buy Now offer for it). Renders as plain text when the tender's factory
// couldn't be matched into the mi_mills directory, rather than linking to a
// page that doesn't exist for it.
const FactoryNameLink = ({ name, slug, navigate, className, style }) => {
  if (!slug) return <span className={className} style={style}>{name}</span>;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); navigate(`/market-intelligence/factory/${slug}`); }}
      className={`text-left hover:underline ${className || ''}`}
      style={{ ...style, color: BLUE }}
    >
      {name}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Desktop (>=1024px): a compact table, one row per grade, with Factory/State/
// Season/Updated Date/Payment/Lifting rowspan-merged across every grade
// row that belongs to the SAME tender event (Factory + exact tender_date_time),
// and rendered per-row instead only for whichever of Season/Payment/Lifting
// genuinely differ within that event. A blue top border marks where each new
// tender event begins, and groups alternate a faint background band so grades
// belonging to one event stay visually clustered even when the same factory
// name repeats a few rows down for an older event.
// ---------------------------------------------------------------------------
const DesktopEventTable = ({ groups, t, navigate }) => {
  const thStyle = { padding: '10px 12px', fontSize: 11, fontWeight: 700, color: MUTED, whiteSpace: 'nowrap', textAlign: 'left' };
  return (
    <div className="hidden lg:block rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: `1px solid ${BORDER}` }}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr style={{ background: CREAM, borderBottom: `1px solid ${BORDER}` }}>
              <th style={thStyle}>{t('common.factory', 'Factory')}</th>
              <th style={thStyle}>{t('marketplace.state', 'State')}</th>
              <th style={thStyle}>{t('marketplace.season', 'Season')}</th>
              <th style={thStyle}>{t('liveTenderInfo.tenderDateTime', 'Updated Date')}</th>
              <th style={thStyle}>{t('marketplace.paymentDate', 'Payment Date')}</th>
              <th style={thStyle}>{t('marketplace.liftingDate', 'Lifting Date')}</th>
              <th style={thStyle}>{t('marketplace.grade', 'Grade')}</th>
              <th style={thStyle}>{t('liveTenderInfo.withoutGstRate', 'Without GST Rate')}</th>
              <th style={thStyle}>{t('common.actions', 'Action')}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => {
              const n = g.records.length;
              const groupBg = gi % 2 === 0 ? '#FFFFFF' : CREAM;
              const approxTender = t('liveTenderInfo.approxDateTime', '{{date}}, around {{time}}', {
                date: fmtDate(g.tenderDateTime),
                time: fmtApproxHour(g.tenderDateTime),
              });
              return g.records.map((r, ri) => {
                const isFirst = ri === 0;
                const cellStyle = {
                  padding: '9px 12px',
                  fontSize: 11.5,
                  color: MUTED,
                  verticalAlign: 'top',
                  borderBottom: `1px solid ${BORDER}`,
                  borderTop: isFirst ? `2px solid ${BLUE}` : 'none',
                  background: groupBg,
                };
                return (
                  <tr key={r.id}>
                    {isFirst && (
                      <td rowSpan={n} style={{ ...cellStyle, fontWeight: 700, maxWidth: 220 }}>
                        <FactoryNameLink name={g.millName} slug={g.directoryFactorySlug} navigate={navigate} style={{ color: NAVY }} />
                      </td>
                    )}
                    {isFirst && (
                      <td rowSpan={n} style={cellStyle}>{g.state}</td>
                    )}
                    {g.sharedSeason ? (
                      isFirst && <td rowSpan={n} style={cellStyle}>{g.sharedSeason}</td>
                    ) : (
                      <td style={cellStyle}>{r.season || ''}</td>
                    )}
                    {isFirst && (
                      <td rowSpan={n} style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{approxTender}</td>
                    )}
                    {g.sharedPaymentDate ? (
                      isFirst && <td rowSpan={n} style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{fmtDate(g.sharedPaymentDate)}</td>
                    ) : (
                      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{r.paymentDate ? fmtDate(r.paymentDate) : ''}</td>
                    )}
                    {g.sharedLiftingDate ? (
                      isFirst && <td rowSpan={n} style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{fmtDate(g.sharedLiftingDate)}</td>
                    ) : (
                      <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{r.liftingDate ? fmtDate(r.liftingDate) : ''}</td>
                    )}
                    <td style={{ ...cellStyle, paddingLeft: 18 }}>
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-black" style={{ background: '#FEF3C7', color: AMBER_DEEP }}>{r.grade}</span>
                    </td>
                    <td style={{ ...cellStyle, fontWeight: 800, color: NAVY, whiteSpace: 'nowrap' }}>{fmtRate(r.rateExclGst)}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}><BuyNowButton tender={r} navigate={navigate} t={t} compact /></td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// One grade row inside a mobile/tablet event card. Grade-specific info
// (season/payment date/lifting date) is only shown here when the group's
// shared value for that field is null — i.e. it genuinely differs (or is
// missing) for at least one grade in this same tender event.
const EventGradeRow = ({ record: r, group, t, navigate }) => {
  const extras = [];
  if (!group.sharedSeason && r.season) extras.push(r.season);
  if (!group.sharedPaymentDate && r.paymentDate) extras.push(`${t('marketplace.paymentDate', 'Payment Date')}: ${fmtDate(r.paymentDate)}`);
  if (!group.sharedLiftingDate && r.liftingDate) extras.push(`${t('marketplace.liftingDate', 'Lifting Date')}: ${fmtDate(r.liftingDate)}`);

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5" style={{ borderBottom: `1px solid ${BORDER}` }}>
      <div className="min-w-0">
        <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-black" style={{ background: '#FEF3C7', color: AMBER_DEEP }}>
          {r.grade}
        </span>
        {extras.length > 0 && (
          <p className="text-[9.5px] mt-1 truncate" style={{ color: FAINT }}>{extras.join(' · ')}</p>
        )}
      </div>
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <span className="text-xs font-black whitespace-nowrap" style={{ color: NAVY }}>{fmtRate(r.rateExclGst)}</span>
        <BuyNowButton tender={r} navigate={navigate} t={t} />
      </div>
    </div>
  );
};

// Mobile/tablet card — one card per tender EVENT (Factory + exact tender_date_time),
// not per factory. The same factory's older tender event is a separate card.
const EventCard = ({ group, index, t, navigate }) => {
  const sharedChips = [];
  if (group.sharedSeason) sharedChips.push({ label: t('marketplace.season', 'Season'), value: group.sharedSeason });
  if (group.sharedPaymentDate) sharedChips.push({ label: t('marketplace.paymentDate', 'Payment Date'), value: fmtDate(group.sharedPaymentDate) });
  if (group.sharedLiftingDate) sharedChips.push({ label: t('marketplace.liftingDate', 'Lifting Date'), value: fmtDate(group.sharedLiftingDate) });

  return (
    <div
      className="card-enter rounded-2xl overflow-hidden"
      style={{
        background: '#FFFFFF',
        border: `1px solid ${BORDER}`,
        borderLeft: `4px solid ${BLUE}`,
        boxShadow: '0 4px 16px rgba(15,34,56,0.08)',
        animationDelay: `${Math.min(index, 8) * 45}ms`,
      }}
    >
      <div className="p-4 pb-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: BLUE_TINT }}>
            <Icon name="Factory" size={17} color={BLUE} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-black leading-snug break-words">
              <FactoryNameLink name={group.millName} slug={group.directoryFactorySlug} navigate={navigate} style={{ color: NAVY }} />
            </h3>
            {group.state && <p className="text-[11px] mt-0.5" style={{ color: FAINT }}>{group.state}</p>}
          </div>
        </div>
        <p className="text-[11px] font-bold mt-2.5" style={{ color: BLUE }}>
          {t('liveTenderInfo.tenderEventTime', 'Updated: {{date}}, around {{time}}', {
            date: fmtDate(group.tenderDateTime),
            time: fmtApproxHour(group.tenderDateTime),
          })}
        </p>
      </div>

      {sharedChips.length > 0 && (
        <div className="px-4 pb-3.5 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {sharedChips.map((c) => (
            <div key={c.label} className="rounded-lg px-2.5 py-2" style={{ background: CREAM }}>
              <p className="text-[9px] font-bold uppercase tracking-wide" style={{ color: FAINT }}>{c.label}</p>
              <p className="text-[11.5px] font-bold mt-0.5" style={{ color: NAVY }}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${BORDER}` }}>
        <div className="flex items-center justify-between gap-3 px-4 py-2" style={{ background: BLUE }}>
          <span className="text-[10px] font-bold uppercase tracking-wide text-white">{t('marketplace.grade', 'Grade')}</span>
          <span className="text-[10px] font-bold uppercase tracking-wide text-white">{t('liveTenderInfo.withoutGstRate', 'Without GST Rate')}</span>
        </div>
        {group.records.map((r) => <EventGradeRow key={r.id} record={r} group={group} t={t} navigate={navigate} />)}
      </div>

      <div className="px-4 py-2.5" style={{ color: FAINT }}>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold">
          <Icon name="ShieldCheck" size={11} color={FAINT} />
          {t('liveTenderInfo.ratesWithoutGst', 'Rates are Without GST')}
        </span>
      </div>
    </div>
  );
};

// Searchable, State-gated Factory combobox. Disabled with clear guidance until a
// State is chosen; once open it always starts from a fresh empty query so typing
// filters the State's factory list rather than editing the previously-picked name.
const FactorySearchSelect = ({ value, onChange, options, disabled, t }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((name) => name.toLowerCase().includes(q));
  }, [options, query]);

  const displayValue = open ? query : (value || '');

  return (
    <div className="relative" ref={rootRef}>
      <input
        type="text"
        disabled={disabled}
        value={displayValue}
        onChange={(e) => { setQuery(e.target.value); if (value) onChange(''); setOpen(true); }}
        onFocus={() => { if (disabled) return; setQuery(''); setOpen(true); }}
        placeholder={disabled ? t('liveTenderInfo.selectStateFirst', 'Select State first') : t('liveTenderInfo.factorySearchPlaceholder', 'Type to search factory')}
        className="text-xs font-semibold rounded-xl px-3 py-2.5 w-full transition-all duration-150"
        style={{ background: '#FFFFFF', border: `1px solid ${value ? AMBER : BORDER}`, color: value ? NAVY : FAINT }}
      />
      {value && !open && (
        <button
          type="button"
          onClick={() => { onChange(''); setQuery(''); }}
          className="absolute right-2.5 top-1/2 -translate-y-1/2"
          aria-label="Clear factory"
        >
          <Icon name="X" size={13} color={FAINT} />
        </button>
      )}
      {open && !disabled && (
        <div
          className="absolute z-40 mt-1 w-full max-h-56 overflow-auto rounded-xl bg-white shadow-lg"
          style={{ border: `1px solid ${BORDER}` }}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2.5 text-xs" style={{ color: FAINT }}>{t('liveTenderInfo.noFactoriesFound', 'No factories found')}</div>
          ) : (
            filtered.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => { onChange(name); setQuery(''); setOpen(false); }}
                className="block w-full text-left px-3 py-2 text-xs hover:bg-[#FDF8F0] transition-colors duration-100"
                style={{ color: NAVY }}
              >
                {name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const LiveTenderInfo = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [tenders, setTenders] = useState([]);
  const [lastCollectedAt, setLastCollectedAt] = useState(null);
  const [mandiIndex, setMandiIndex] = useState(null);
  const [directoryIndex, setDirectoryIndex] = useState(null);

  // Date-window bookkeeping for "latest 7 days, Load More in 7-day blocks,
  // never past the start of the relevant month" — see dateWindow.js.
  // monthStart/loadedStart are IST {year,month,day} parts, not Date objects.
  const [monthStartParts, setMonthStartParts] = useState(null);
  const [loadedStartParts, setLoadedStartParts] = useState(null);

  const [state, setState] = useState('');
  const [factory, setFactory] = useState('');
  const [grade, setGrade] = useState('');
  const [season, setSeason] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);

      const [{ latestTenderDate, error: latestErr }, { lastCollectedAt: lc }, { mandiIndex: mi, error: mandiErr }, { directoryIndex: di }] = await Promise.all([
        fetchLatestTenderDate(),
        fetchLastCollectedAt(),
        fetchMandiIndex(),
        fetchFactoryDirectoryIndex(),
      ]);
      if (cancelled) return;

      if (latestErr || mandiErr) {
        setError(latestErr || mandiErr);
        setLoading(false);
        return;
      }

      setLastCollectedAt(lc);
      setMandiIndex(mi);
      // A failed directory lookup only means factory names render as plain text
      // instead of links — it should never block the tender listing itself.
      setDirectoryIndex(di);

      if (!latestTenderDate) {
        setTenders([]);
        setLoading(false);
        return;
      }

      const latest = getISTDateParts(new Date(latestTenderDate));
      const monthStart = { year: latest.year, month: latest.month, day: 1 };
      const initialStart = laterOf(monthStart, addDays(latest, -6));

      const { tenders: rows, error: blockErr } = await fetchTenderBlock({
        startISO: istMidnightISO(initialStart),
        endExclusiveISO: istMidnightISO(addDays(latest, 1)),
        mandiIndex: mi,
        directoryIndex: di,
      });
      if (cancelled) return;
      if (blockErr) {
        setError(blockErr);
        setLoading(false);
        return;
      }

      setTenders(rows);
      setMonthStartParts(monthStart);
      setLoadedStartParts(initialStart);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const hasMore = !!(loadedStartParts && monthStartParts && compareDateParts(loadedStartParts, monthStartParts) > 0);

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    const newStart = laterOf(monthStartParts, addDays(loadedStartParts, -7));
    const { tenders: rows, error: blockErr } = await fetchTenderBlock({
      startISO: istMidnightISO(newStart),
      endExclusiveISO: istMidnightISO(loadedStartParts),
      mandiIndex,
      directoryIndex,
    });
    if (blockErr) {
      setError(blockErr);
    } else {
      // Blocks are disjoint date ranges — appending never duplicates or loses a record.
      setTenders((prev) => [...prev, ...rows]);
      setLoadedStartParts(newStart);
    }
    setLoadingMore(false);
  };

  const handleStateChange = (v) => { setState(v); setFactory(''); setGrade(''); setSeason(''); };
  const handleFactoryChange = (v) => { setFactory(v); setGrade(''); setSeason(''); };

  const states = useMemo(
    () => [...new Set(tenders.map((r) => r.state).filter(Boolean))].sort(),
    [tenders]
  );

  const stateFiltered = useMemo(
    () => tenders.filter((r) => !state || r.state === state),
    [tenders, state]
  );

  const factoryNames = useMemo(
    () => [...new Set(stateFiltered.map((r) => r.millName))].sort(),
    [stateFiltered]
  );

  const factoryFiltered = useMemo(
    () => stateFiltered.filter((r) => !factory || r.millName === factory),
    [stateFiltered, factory]
  );

  const grades = useMemo(
    () => [...new Set(factoryFiltered.map((r) => r.grade).filter(Boolean))].sort(),
    [factoryFiltered]
  );

  const seasons = useMemo(
    () => [...new Set(factoryFiltered.map((r) => r.season).filter(Boolean))].sort(),
    [factoryFiltered]
  );

  // 1. fetch (existing range logic) -> 2. state/factory filter -> 3. grade/season/search
  // filter -> 4. (matching already applied per-record in data.js) -> 5. group by
  // Factory + exact tender_date_time for presentation only.
  const filtered = useMemo(() => factoryFiltered.filter((r) => {
    if (grade && r.grade !== grade) return false;
    if (season && r.season !== season) return false;
    if (search && !r.millName.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  }), [factoryFiltered, grade, season, search]);

  const groups = useMemo(() => buildTenderEventGroups(filtered), [filtered]);

  const hasActiveFilters = state || factory || grade || season || search;

  // Clears filters only — the loaded date range (and Load More progress) is
  // deliberately left alone, matching the existing rolling-7-day concept.
  const clearFilters = () => { setState(''); setFactory(''); setGrade(''); setSeason(''); setSearch(''); };

  const updatedAround = formatUpdatedAround(lastCollectedAt);

  const resultCountText = groups.length === 1
    ? t('liveTenderInfo.listingsAcrossOneEvent', '{{count}} tender listings across 1 tender event', { count: filtered.length })
    : t('liveTenderInfo.listingsAcrossEvents', '{{count}} tender listings across {{events}} tender events', { count: filtered.length, events: groups.length });

  return (
    <div className="min-h-screen" style={{ background: '#F3EFE4', fontFamily: "'DM Sans', sans-serif" }}>
      <Helmet>
        <title>{t('liveTenderInfo.title', 'Live Tender Info')} — SugarHathi</title>
        <meta name="description" content={t('liveTenderInfo.description', 'Latest sugar factory tender information and market updates.')} />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      <GlobalStyle />

      <div className="sticky top-0 z-30 bg-white border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-white" style={{ border: `1px solid ${BORDER}` }}>
              <img src={LOGO_URL} alt="SugarHathi logo" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-black" style={{ color: BLUE }}>{t('liveTenderInfo.title', 'Live Tender Info')}</p>
              <p className="text-[10px] truncate" style={{ color: FAINT }}>
                {t('liveTenderInfo.subtitle', 'Recent tender activity — last 7 days')}
                {updatedAround ? ` · ${t('liveTenderInfo.updatedAround', 'Updated around {{time}}', { time: updatedAround })}` : ''}
              </p>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
        <div className="rounded-2xl p-4 mb-5" style={{ background: '#FFFFFF', border: `1px solid ${BORDER}` }}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 items-start">
            <select
              value={state}
              onChange={(e) => handleStateChange(e.target.value)}
              className="text-xs font-semibold rounded-xl px-3 py-2.5 w-full transition-all duration-150"
              style={{ background: '#FFFFFF', border: `1px solid ${state ? AMBER : BORDER}`, color: state ? NAVY : FAINT }}
            >
              <option value="">{t('marketplace.state', 'State')}</option>
              {states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>

            <FactorySearchSelect value={factory} onChange={handleFactoryChange} options={factoryNames} disabled={!state} t={t} />

            <select
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="text-xs font-semibold rounded-xl px-3 py-2.5 w-full transition-all duration-150"
              style={{ background: '#FFFFFF', border: `1px solid ${grade ? AMBER : BORDER}`, color: grade ? NAVY : FAINT }}
            >
              <option value="">{t('marketplace.grade', 'Grade')}</option>
              {grades.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>

            <select
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              className="text-xs font-semibold rounded-xl px-3 py-2.5 w-full transition-all duration-150"
              style={{ background: '#FFFFFF', border: `1px solid ${season ? AMBER : BORDER}`, color: season ? NAVY : FAINT }}
            >
              <option value="">{t('marketplace.season', 'Season')}</option>
              {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="relative mt-2.5">
            <Icon name="Search" size={13} color={FAINT} className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('liveTenderInfo.searchPlaceholder', 'Search by factory name')}
              className="text-xs font-semibold rounded-xl pl-8 pr-3 py-2.5 w-full transition-all duration-150"
              style={{ background: '#FFFFFF', border: `1px solid ${search ? AMBER : BORDER}`, color: NAVY }}
            />
          </div>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-[11px] font-semibold mt-2.5 hover:underline" style={{ color: AMBER_DEEP }}>
              {t('liveTenderInfo.clearFilters', 'Clear Filters')}
            </button>
          )}
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-8 h-8 rounded-full border-2 animate-spin mb-3" style={{ borderColor: '#fde68a', borderTopColor: AMBER }} />
            <p className="text-sm" style={{ color: MUTED }}>{t('liveTenderInfo.loadingTenders', 'Loading tender listings…')}</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Icon name="AlertTriangle" size={28} color="#DC2626" />
            <p className="text-sm mt-3" style={{ color: MUTED }}>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <p className="text-xs font-semibold mb-3" style={{ color: MUTED }}>{resultCountText}</p>

            {filtered.length === 0 ? (
              <div className="text-center py-16 rounded-2xl" style={{ background: '#FFFFFF', border: `1px solid ${BORDER}` }}>
                <p className="text-sm" style={{ color: FAINT }}>
                  {tenders.length === 0 ? t('liveTenderInfo.noRecentTenders', 'No tender data from the last 7 days yet.') : t('liveTenderInfo.noOffersFound', 'No tender listings match these filters.')}
                </p>
              </div>
            ) : (
              <>
                {/* Desktop / laptop (>=1024px): compact scannable table, grouped by tender event */}
                <DesktopEventTable groups={groups} t={t} navigate={navigate} />

                {/* Tablet / mobile (<1024px): one card per tender event */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start lg:hidden">
                  {groups.map((g, i) => <EventCard key={g.key} group={g} index={i} t={t} navigate={navigate} />)}
                </div>
              </>
            )}

            {hasMore && (
              <div className="flex justify-center mt-5">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all duration-150 hover:opacity-90 disabled:opacity-60"
                  style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, color: NAVY }}
                >
                  {loadingMore && <div className="w-3.5 h-3.5 rounded-full border-2 animate-spin" style={{ borderColor: '#fde68a', borderTopColor: AMBER }} />}
                  {t('liveTenderInfo.loadMore', 'Load More')}
                </button>
              </div>
            )}
          </>
        )}

        <div className="rounded-2xl p-4 mt-6" style={{ background: BLUE_TINT, border: '1px solid #BFDBFE' }}>
          <p className="text-xs leading-relaxed" style={{ color: BLUE }}>
            {t('liveTenderInfo.disclaimer', "Data and information displayed on this platform may be sourced from third-party sources and are provided for general informational purposes only. Such information is not owned, controlled or guaranteed by SugarHathi. SugarHathi does not represent third-party data as its own offering and does not monetise the underlying third-party information.")}
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LiveTenderInfo;
